// @/src/core/utils/ui.ts
import { playSuccessSound } from '@/core/services/audio.service';

/**
 * @file src/core/utils/ui.ts
 * @description Funciones para controlar elementos globales de la interfaz de usuario.
 */

type ToastType = 'success' | 'error' | 'info' | 'warning';

// --- Modales ---
export interface ModalToggleOptions {
    activeClass?: string;
    display?: string;
    duration?: number;
}

export function showModal(modalElement: HTMLElement | null, opts: ModalToggleOptions = {}): void {
    if (modalElement) {
        const { activeClass = 'show', display = 'flex' } = opts;
        modalElement.style.display = display;
        setTimeout(() => modalElement.classList.add(activeClass), 10);
    }
}

export function hideModal(modalElement: HTMLElement | null, opts: ModalToggleOptions = {}): void {
    if (modalElement) {
        const { activeClass = 'show', duration = 300 } = opts;
        modalElement.classList.remove(activeClass);
        setTimeout(() => { modalElement.style.display = 'none'; }, duration);
    }
}

export function showConfirmationModal(message: string, onConfirm: () => void): void {
    const modal = document.getElementById('confirmationModal');
    const messageElement = document.getElementById('confirmationModalMessage');
    const confirmBtn = document.getElementById('confirmationModalConfirmBtn');
    const cancelBtn = document.getElementById('confirmationModalCancelBtn');
    const closeBtn = document.getElementById('confirmationModalCloseBtn');

    if (!modal || !messageElement || !confirmBtn || !cancelBtn || !closeBtn) {
        if (confirm(message)) onConfirm();
        return;
    }

    messageElement.textContent = message;
    const newConfirmBtn = confirmBtn.cloneNode(true);
    confirmBtn.parentNode?.replaceChild(newConfirmBtn, confirmBtn);
    
    const hide = () => hideModal(modal);
    newConfirmBtn.addEventListener('click', () => { hide(); onConfirm(); });
    cancelBtn.onclick = hide;
    closeBtn.onclick = hide;
    showModal(modal);
}

// --- Notificaciones y Overlays ---
export function showToast(message: string, type: ToastType = 'success', duration: number = 3000): void {
    const toast = document.getElementById('toast');
    const msg = document.getElementById('toastMessage');
    const icon = document.getElementById('toastIcon');
    if (!toast || !msg || !icon) return;

    msg.innerHTML = message;
    toast.className = 'toast';
    icon.className = 'fas';

    const typeClasses: { [key in ToastType]: string } = {
        success: 'success fa-check-circle',
        error: 'error fa-exclamation-circle',
        info: 'info fa-info-circle',
        warning: 'warning fa-exclamation-triangle'
    };
    toast.classList.add(type);

    if (type === 'warning' && message.toLowerCase().includes('relacion')) {
        icon.style.display = 'none';
    } else {
        icon.style.display = 'block';
        icon.classList.add(...typeClasses[type].split(' ').slice(1));
    }
    
    toast.classList.add('show');
    setTimeout(() => toast.classList.remove('show'), duration);
}

/**
 * Muestra la animación de venta procesada y reproduce un sonido de éxito.
 * La hace visible, activa la animación CSS y la oculta después de 2.5 segundos.
 */
export function showSuccessAnimation(): void {
    const ventaProcesadaAnimacionDiv = document.getElementById('ventaProcesadaAnimacion');
    if (!ventaProcesadaAnimacionDiv) return;

    // Reproducir el sonido
    playSuccessSound();

    // 1. Hacer el contenedor visible
    ventaProcesadaAnimacionDiv.style.display = 'flex';
    
    // 2. Añadir la clase 'show' para iniciar la animación de entrada
    setTimeout(() => {
        ventaProcesadaAnimacionDiv.classList.add('show');
    }, 10);

    // 3. Después de 2.5 segundos, iniciar la animación de salida
    setTimeout(() => {
        ventaProcesadaAnimacionDiv.classList.remove('show');
        
        // 4. Esperar a que termine la animación de salida para ocultar el div
        setTimeout(() => {
            ventaProcesadaAnimacionDiv.style.display = 'none';
        }, 300);
    }, 2500);
}

// --- Formularios ---
export function validateField(field: HTMLInputElement | HTMLSelectElement, validationFn: (value: string) => boolean, errorMessage: string = 'Este campo es obligatorio'): boolean {
    if (validationFn(field.value)) {
        setFieldError(field, '');
        return true;
    }
    setFieldError(field, errorMessage);
    return false;
}

export function validateForm(form: HTMLFormElement): boolean {
    const fields = form.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[required]');
    let allValid = true;
    fields.forEach(field => {
        if (!validateField(field, (v) => v.trim() !== '')) {
            allValid = false;
        }
    });
    return allValid;
}

export function setFieldError(field: HTMLElement, message: string): void {
    const errorElement = field.parentElement?.querySelector('.field-error-message');
    if (message) {
        field.classList.add('input-error');
        if (errorElement) errorElement.textContent = message;
    } else {
        field.classList.remove('input-error');
        if (errorElement) errorElement.textContent = '';
    }
}

export function clearFormErrors(form: HTMLFormElement): void {
    form.querySelectorAll('input, select, textarea').forEach(field => setFieldError(field as HTMLElement, ''));
}

// --- UI Dinámica ---
export function createEmptyState(iconClass: string, title: string, subtitle: string): string {
    return `
        <div class="empty-state-content">
            <i class="fas ${iconClass} text-4xl text-gray-400 mb-4"></i>
            <h3 class="text-lg font-medium text-gray-700">${title}</h3>
            <p class="text-gray-500 mt-1">${subtitle}</p>
        </div>`;
}

export function renderizarAvatar(
    imgElement: HTMLImageElement | null, 
    initialsElement: HTMLElement | null, 
    fotoURL: string | null | undefined, 
    nombre: string,
    defaultImage: string | null = null
): void {
    if (!imgElement) return;

    if (initialsElement) {
        const iniciales = nombre.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() || 'QP';
        initialsElement.textContent = iniciales;
    }

    if (fotoURL) {
        imgElement.src = fotoURL;
        imgElement.classList.remove('hidden');
        if (initialsElement) initialsElement.classList.add('hidden');
        imgElement.onerror = () => {
            if (defaultImage) {
                imgElement.src = defaultImage;
                // No ocultar si la imagen por defecto funciona
            } else {
                imgElement.classList.add('hidden');
                if (initialsElement) initialsElement.classList.remove('hidden');
            }
        };
    } else if (defaultImage) {
        imgElement.src = defaultImage;
        imgElement.classList.remove('hidden');
        if (initialsElement) initialsElement.classList.add('hidden');
        imgElement.onerror = () => {
            // Si incluso la imagen por defecto falla, mostrar iniciales
            imgElement.classList.add('hidden');
            if (initialsElement) initialsElement.classList.remove('hidden');
        };
    } else {
        imgElement.classList.add('hidden');
        if (initialsElement) initialsElement.classList.remove('hidden');
    }
}

/**
 * Elimina los atributos title de los botones action-btn y los convierte a data-tooltip
 * para evitar que aparezcan tooltips nativos del navegador junto con los personalizados.
 */
export function removeNativeTooltips(): void {
    const actionButtons = document.querySelectorAll('.action-btn[title]');
    actionButtons.forEach((button) => {
        const btn = button as HTMLElement;
        const title = btn.getAttribute('title');
        if (title && !btn.hasAttribute('data-tooltip')) {
            // Si tiene title pero no data-tooltip, convertir title a data-tooltip
            btn.setAttribute('data-tooltip', title);
        }
        // Eliminar el atributo title para evitar el tooltip nativo
        btn.removeAttribute('title');
    });
}
